import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminShell from "../components/admin/AdminShell";
import { apiFetch, useAuth } from "../hooks/useAuth";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const day = String(d.getUTCDate()).padStart(2, "0");
  const mon = months[d.getUTCMonth()];
  const yr = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day} ${mon} ${yr} · ${hh}:${mm} UTC`;
};

const Meta = ({ label, value, testId }) => (
  <div>
    <p className="font-sans uppercase text-navy/55" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
      {label}
    </p>
    <p data-testid={testId} className="mt-1.5 font-sans text-navy" style={{ fontSize: "15px" }}>
      {value || "—"}
    </p>
  </div>
);

const StatusPill = ({ status }) => {
  const isNew = status === "new";
  return (
    <span
      data-testid={`detail-status-${status}`}
      className={`inline-block px-2.5 py-1 border font-sans uppercase ${
        isNew ? "border-gold text-gold" : "border-navy text-navy"
      }`}
      style={{ fontSize: "10px", letterSpacing: "0.22em", borderRadius: 2 }}
    >
      {isNew ? "New" : "Reviewed"}
    </span>
  );
};

const AdminSubmissionDetail = () => {
  const nav = useNavigate();
  const { id } = useParams();
  const { loading, user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justChanged, setJustChanged] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [loading, user, nav]);

  const load = () => {
    apiFetch(`/api/admin/submissions/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setDoc)
      .catch((e) => {
        if (e === 401) nav("/admin/login");
        else setError("Not found.");
      });
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const toggle = async () => {
    if (!doc) return;
    setBusy(true);
    const action = doc.status === "new" ? "review" : "unreview";
    const res = await apiFetch(`/api/admin/submissions/${id}/${action}`, { method: "POST" });
    if (res.ok) {
      const next = await res.json();
      setDoc(next);
      setJustChanged({
        action,
        at: next.reviewed_at,
        by: next.reviewed_by || user,
      });
    }
    setBusy(false);
  };

  if (loading || !user) return null;
  if (error) {
    return (
      <AdminShell user={user}>
        <p className="font-serif text-2xl text-navy">{error}</p>
        <Link to="/admin/submissions" className="mt-6 inline-block font-sans uppercase text-navy/85 hover:text-gold" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
          ← Back to inquiries
        </Link>
      </AdminShell>
    );
  }
  if (!doc) {
    return (
      <AdminShell user={user}>
        <p className="font-sans text-navy/55" style={{ fontSize: "13px" }}>Loading…</p>
      </AdminShell>
    );
  }

  return (
    <AdminShell user={user}>
      <Link
        to="/admin/submissions"
        data-testid="back-to-list"
        className="group inline-flex items-center gap-2 font-sans uppercase text-navy/85 hover:text-gold transition-colors"
        style={{ fontSize: "11px", letterSpacing: "0.22em" }}
      >
        <span aria-hidden="true" className="inline-block transition-transform duration-300 group-hover:-translate-x-1">←</span>
        <span>Back to inquiries</span>
      </Link>

      <div className="mt-12 flex items-center gap-5">
        <span className="font-sans uppercase text-gold" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
          Inquiry · {doc.id.slice(0, 6)}
        </span>
        <StatusPill status={doc.status} />
      </div>
      <h1
        className="mt-6 font-serif font-normal text-navy"
        data-testid="detail-name"
        style={{ fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.08, letterSpacing: "-0.01em" }}
      >
        {doc.name}
      </h1>
      <span aria-hidden="true" className="mt-8 block h-px w-8 bg-gold" />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="flex flex-col gap-7">
          <Meta label="Company" value={doc.company} testId="detail-company" />
          <Meta label="Email" value={doc.email} testId="detail-email" />
          <Meta label="Phone" value={doc.phone} testId="detail-phone" />
        </div>
        <div className="flex flex-col gap-7">
          <Meta label="Received" value={fmtDateTime(doc.submitted_at)} testId="detail-received" />
          <Meta label="Status" value={doc.status === "new" ? "New" : "Reviewed"} />
          {doc.status === "reviewed" ? (
            <Meta label="Reviewed" value={`${doc.reviewed_by || "—"} · ${fmtDateTime(doc.reviewed_at)}`} testId="detail-reviewed" />
          ) : null}
        </div>
      </div>

      <span aria-hidden="true" className="mt-14 block w-full h-px bg-navy/8" />

      <div className="mt-10 max-w-[65ch]">
        <p
          data-testid="detail-message"
          className="font-sans text-navy/85"
          style={{ fontSize: "16px", lineHeight: 1.7, whiteSpace: "pre-wrap" }}
        >
          {doc.message}
        </p>
      </div>

      <div className="mt-16">
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          data-testid="toggle-status"
          className="group relative inline-flex items-center justify-center px-7 py-3.5 border border-navy/70 hover:border-gold text-navy font-sans uppercase transition-colors duration-300 disabled:opacity-60"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          <span className="relative">
            {busy ? "Saving…" : doc.status === "new" ? "Mark as Reviewed" : "Mark as New"}
            <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100" />
          </span>
        </button>
        {justChanged ? (
          <p className="mt-5 font-serif italic text-navy/65" style={{ fontSize: "15px" }}>
            {justChanged.action === "review"
              ? `Marked as reviewed by ${justChanged.by} at ${fmtDateTime(justChanged.at)}.`
              : `Returned to new by ${user}.`}
          </p>
        ) : null}
      </div>
    </AdminShell>
  );
};

export default AdminSubmissionDetail;
